import React from "react";
import { Form } from "react-bootstrap";
import { withTranslation, WithTranslation } from "react-i18next";
import { OutputSettingsBaseCard, OutputSettingsBaseCardProps } from "./OutputSettingsBaseCard";
import { DEFAULT_DELETION_RULE, TRIAL_DELETION_RULE } from "../useCreateWizard";

interface DeletionSettingsCardProps extends OutputSettingsBaseCardProps, WithTranslation {
    isTrialMode: boolean;
}

class DeletionSettingsCardComponent extends OutputSettingsBaseCard<DeletionSettingsCardProps> {
    protected getTitleTranslationKey(): string {
        return "cards.deletion.title";
    }

    protected renderCardBody(): React.ReactNode {
        const { outputSettings, t, isTrialMode } = this.props;

        // Auto-select trial deletion rule for trial mode users
        let selectedRule = outputSettings.deletionRule || DEFAULT_DELETION_RULE;
        if (isTrialMode && selectedRule !== TRIAL_DELETION_RULE) {
            selectedRule = TRIAL_DELETION_RULE;
            // Update the parent component with the forced selection
            this.handleSettingChange("deletionRule", TRIAL_DELETION_RULE);
        }

        const isTrialViolation = isTrialMode && selectedRule !== TRIAL_DELETION_RULE;

        return (
            <>
                <Form.Group className="mb-3">
                    <Form.Label>{t("common:deletionRule.title")}</Form.Label>
                    <Form.Select
                        value={selectedRule}
                        onChange={(e) => this.handleSettingChange("deletionRule", e.target.value)}
                        required={true}
                        className={isTrialViolation ? "is-invalid" : ""}
                    >
                        <option key="NONE" value="NONE">
                            {t("common:deletionRule.ruleOptions.NONE")}
                        </option>
                        <option key="1WEEK" value="1WEEK">
                            {t("common:deletionRule.ruleOptions.1WEEK")}
                        </option>
                        <option key="2WEEK" value="2WEEK">
                            {t("common:deletionRule.ruleOptions.2WEEK")}
                        </option>
                        <option key="1MONTH" value="1MONTH">
                            {t("common:deletionRule.ruleOptions.1MONTH")}
                        </option>
                        <option key="3MONTHS" value="3MONTHS">
                            {t("common:deletionRule.ruleOptions.3MONTHS")}
                        </option>
                        <option key="6MONTHS" value="6MONTHS">
                            {t("common:deletionRule.ruleOptions.6MONTHS")}
                        </option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                        {isTrialViolation ? t("common:trialMode.deletionRestriction") : t("cards.naming.required")}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">{t("cards.deletion.description")}</Form.Text>
                </Form.Group>
                {this.renderDeletionDateField()}
            </>
        );
    }

    private renderDeletionDateField(): React.ReactNode {
        const { outputSettings, selectedEvent, t } = this.props;

        if (
            !outputSettings.deletionRule ||
            outputSettings.deletionRule === "NONE" ||
            !selectedEvent?.customFieldDefinitions
        ) {
            return null;
        }

        const dateFields = selectedEvent.customFieldDefinitions
            .filter((fieldDef) => fieldDef.type === "DATE")
            .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0));

        return this.renderCustomFieldSelector({
            texts: {
                label: t("cards.deletion.dateFieldLabel"),
                description: t("cards.deletion.dateFieldDescription"),
                noFieldsMessage: t("cards.deletion.noFieldsMessage"),
            },
            customFields: dateFields,
            selectedFieldId: outputSettings.deleteDateFieldDefinitionId,
            onFieldIdChange: (value) => this.handleSettingChange("deleteDateFieldDefinitionId", value),
        });
    }
}

export const DeletionSettingsCard = withTranslation(["wizard", "common"])(DeletionSettingsCardComponent);
